import { useCallback, useState } from "react"
import { useToast } from "@mallardbay/lib-react-components"

import {
    isImageAttachmentMediaType,
    toChatFileAttachment,
    toFileUIPart,
    type ChatFileAttachment,
    type ChatUploadedFile,
} from "~components/shared/chat/chat-file-attachment-helpers"
import useChatPastedFileUpload from "~components/shared/chat/use-chat-pasted-file-upload"
import COPY from "~config/copy-constants"
import type useAiChat from "~utils/hooks/use-ai-chat"

interface Props {
    readonly sendMessage: ReturnType<typeof useAiChat>["sendMessage"]
    readonly isLoading: boolean
}

export default function useChatInputState({ sendMessage, isLoading }: Props) {
    const { showError } = useToast()
    const [message, setMessage] = useState("")
    const [pendingAttachments, setPendingAttachments] = useState<
        ChatFileAttachment[]
    >([])

    const handleNewFiles = useCallback(
        (files: ChatUploadedFile[]) => {
            const { attachments, rejectedCount } = partitionUploadedFiles(files)

            if (rejectedCount > 0) {
                showError(COPY.AI_CHAT_UNSUPPORTED_ATTACHMENT)
            }

            if (!attachments.length) return

            setPendingAttachments((current) =>
                dedupeAttachments([...current, ...attachments])
            )
        },
        [showError]
    )

    const { handlePastedFiles, uploadingCount } = useChatPastedFileUpload({
        onUploaded: handleNewFiles,
    })

    // Block sending while pasted files are still uploading so a message can
    // never go out without the attachments the user expects it to carry.
    const canSubmit =
        !uploadingCount && Boolean(message.trim() || pendingAttachments.length)

    const handleSubmit = useCallback(async () => {
        if (!canSubmit || isLoading) return

        const textToSend = message.trim()
        const fileParts = pendingAttachments.map(toFileUIPart)

        setMessage("")
        setPendingAttachments([])

        if (textToSend) {
            await sendMessage({
                text: textToSend,
                files: fileParts.length ? fileParts : undefined,
            })
            return
        }

        await sendMessage({ files: fileParts })
    }, [canSubmit, isLoading, message, pendingAttachments, sendMessage])

    const handleRemoveAttachment = useCallback((attachmentId: string) => {
        setPendingAttachments((current) =>
            current.filter((attachment) => attachment.id !== attachmentId)
        )
    }, [])

    const handleSelectImageUrls = useCallback((imageUrls: string[]) => {
        setPendingAttachments((current) => [
            ...current.filter(
                (attachment) =>
                    !isImageAttachmentMediaType(attachment.mediaType)
            ),
            ...imageUrls.map(toImageAttachment),
        ])
    }, [])

    return {
        message,
        setMessage,
        pendingAttachments,
        canSubmit,
        uploadingCount,
        handleSubmit,
        handlePastedFiles,
        handleRemoveAttachment,
        handleSelectImageUrls,
    }
}

function partitionUploadedFiles(files: ChatUploadedFile[]) {
    const attachments: ChatFileAttachment[] = []
    let rejectedCount = 0

    files.forEach((file) => {
        const attachment = toChatFileAttachment(file)
        if (!attachment) {
            rejectedCount += 1
            return
        }

        attachments.push(attachment)
    })

    return { attachments, rejectedCount }
}

function dedupeAttachments(attachments: ChatFileAttachment[]) {
    const seen = new Set<string>()

    return attachments.filter((attachment) => {
        if (seen.has(attachment.id)) return false
        seen.add(attachment.id)
        return true
    })
}

function toImageAttachment(url: string): ChatFileAttachment {
    return {
        id: url,
        url,
        filename: getFileNameFromUrl(url),
        mediaType: "image/jpeg",
    }
}

function getFileNameFromUrl(url: string) {
    try {
        const pathname = new URL(url).pathname
        return pathname.split("/").pop() || "attachment"
    } catch {
        return url.split("/").pop() || "attachment"
    }
}
